import {
  ChevronDownIcon,
  ChevronRightIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import React, { useMemo, useState } from "react";
import { Badge, Button, Checkbox, Input, Modal } from "../../../shared";
import { renderAmount } from "../../../utils";
import {
  formatTaxRatePercent,
  getJobDisplayNumber,
  parseTaxRatePercent,
  toNumber,
} from "../utils";

function buildJobLabel(job = {}) {
  return `${getJobDisplayNumber(job)} • ${renderAmount(
    job.totalCharges || job.calculatedTotalCharges || 0,
  )}`;
}

function matchesJobSearch(job, searchTerm) {
  const normalizedSearch = String(searchTerm || "")
    .trim()
    .toLowerCase();
  if (!normalizedSearch) {
    return true;
  }

  const searchableValues = [
    job.jobId,
    job.loadNumber,
    job._id,
    job.description,
    job.client?.name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return searchableValues.includes(normalizedSearch);
}

function DetailField({ label, children, className = "" }) {
  return (
    <div className={className}>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </p>
      {children}
    </div>
  );
}

function IconActionButton({
  label,
  icon: Icon,
  onClick,
  disabled = false,
  tone = "default",
}) {
  return (
    <button
      type="button"
      className={`inline-flex h-8 w-8 items-center justify-center rounded-full border p-2 transition ${
        tone === "danger"
          ? "border-red-200 text-red-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
          : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-100 hover:text-gray-700"
      } ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

const LineItemsEditor = ({
  lineItems,
  jobs,
  onChange,
  onRemove,
  onAddManual,
  onAddJobs,
}) => {
  const [jobPickerOpen, setJobPickerOpen] = useState(false);
  const [jobSearch, setJobSearch] = useState("");
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [expandedLineItemIds, setExpandedLineItemIds] = useState([]);

  const selectedSourceIds = useMemo(
    () =>
      new Set(
        (lineItems || [])
          .filter(
            (lineItem) => lineItem.sourceType === "job" && lineItem.sourceId,
          )
          .map((lineItem) => String(lineItem.sourceId)),
      ),
    [lineItems],
  );

  const availableJobs = useMemo(
    () => (jobs || []).filter((job) => !selectedSourceIds.has(String(job._id))),
    [jobs, selectedSourceIds],
  );

  const filteredJobs = useMemo(
    () => availableJobs.filter((job) => matchesJobSearch(job, jobSearch)),
    [availableJobs, jobSearch],
  );

  function toggleJobSelection(jobId, checked) {
    setSelectedJobIds((current) => {
      const nextIds = new Set(current);
      if (checked) {
        nextIds.add(String(jobId));
      } else {
        nextIds.delete(String(jobId));
      }
      return Array.from(nextIds);
    });
  }

  function closeJobPicker() {
    setJobPickerOpen(false);
    setJobSearch("");
    setSelectedJobIds([]);
  }

  function confirmJobSelection() {
    const selectedJobs = availableJobs.filter((job) =>
      selectedJobIds.includes(String(job._id)),
    );

    if (!selectedJobs.length) {
      return;
    }

    onAddJobs(selectedJobs);
    closeJobPicker();
  }

  function toggleLineItemDetails(localId) {
    setExpandedLineItemIds((current) =>
      current.includes(localId)
        ? current.filter((id) => id !== localId)
        : [...current, localId],
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Line items</h2>
            <p className="mt-1 text-sm text-gray-500">
              Add custom charges directly or pull eligible jobs into the
              invoice.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              type="alternative"
              size="sm"
              onClick={() => setJobPickerOpen(true)}
            >
              Add jobs
            </Button>
            <Button size="sm" onClick={onAddManual}>
              Add custom charge
            </Button>
          </div>
        </div>
      </div>

      {lineItems.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-500">
          No line items yet. Start by adding a custom charge or selecting
          billable jobs.
        </div>
      ) : null}

      {lineItems.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="hidden grid-cols-12 gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 md:grid">
            <p className="col-span-5">Description</p>
            <p className="col-span-2">Qty</p>
            <p className="col-span-2">Rate</p>
            <p className="col-span-2 text-right">Amount</p>
            <p className="col-span-1 text-right">Actions</p>
          </div>

          {lineItems.map((lineItem, index) => {
            const linkedJob = jobs.find(
              (job) => String(job._id) === String(lineItem.sourceId || ""),
            );
            const jobLabel =
              buildJobLabel(
                linkedJob || {
                  _id: lineItem.sourceId,
                  jobId: lineItem.metadata?.jobNumber,
                  loadNumber: lineItem.metadata?.loadNumber,
                  totalCharges: lineItem.amount,
                },
              ) || "Linked job";
            const isJobBacked = lineItem.sourceType === "job";
            const isExpanded = expandedLineItemIds.includes(lineItem.localId);
            const DetailsIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;

            return (
              <div
                key={lineItem.localId}
                className={
                  index === lineItems.length - 1
                    ? ""
                    : "border-b border-gray-100"
                }
              >
                <div className="grid grid-cols-12 gap-3 px-4 py-3">
                  <div className="col-span-12 md:col-span-5">
                    <Input
                      size="sm"
                      wrapperClasses="mb-0"
                      value={lineItem.description}
                      placeholder="What is being billed?"
                      onChange={(target) =>
                        onChange(lineItem.localId, {
                          description: target.value,
                        })
                      }
                    />
                    <div className="mt-1 flex min-w-0 items-center gap-2">
                      <Badge color={isJobBacked ? "blue" : "slate"}>
                        {isJobBacked ? "Job" : "Custom"}
                      </Badge>
                      <p className="truncate text-xs text-gray-500">
                        {isJobBacked ? jobLabel : `Line item ${index + 1}`}
                      </p>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                        onClick={() => toggleLineItemDetails(lineItem.localId)}
                      >
                        <DetailsIcon className="h-3.5 w-3.5" />
                        {isExpanded ? "Hide" : "Details"}
                      </button>
                    </div>
                  </div>

                  <div className="col-span-4 md:col-span-2">
                    <Input
                      size="sm"
                      wrapperClasses="mb-0"
                      type="number"
                      placeholder="Qty"
                      value={lineItem.quantity}
                      onChange={(target) => {
                        const quantity = toNumber(target.value);
                        onChange(lineItem.localId, {
                          quantity,
                          amount: quantity * toNumber(lineItem.unitPrice),
                        });
                      }}
                    />
                  </div>

                  <div className="col-span-4 md:col-span-2">
                    <Input
                      size="sm"
                      wrapperClasses="mb-0"
                      type="number"
                      placeholder="Rate"
                      value={lineItem.unitPrice}
                      onChange={(target) => {
                        const unitPrice = toNumber(target.value);
                        onChange(lineItem.localId, {
                          unitPrice,
                          amount: toNumber(lineItem.quantity) * unitPrice,
                        });
                      }}
                    />
                  </div>

                  <div className="col-span-4 md:col-span-2">
                    <Input
                      size="sm"
                      wrapperClasses="mb-0"
                      type="number"
                      placeholder="Amount"
                      value={lineItem.amount}
                      onChange={(target) =>
                        onChange(lineItem.localId, {
                          amount: toNumber(target.value),
                        })
                      }
                    />
                  </div>

                  <div className="col-span-12 flex items-start justify-end gap-2 md:col-span-1">
                    <IconActionButton
                      label="Remove line item"
                      icon={TrashIcon}
                      tone="danger"
                      onClick={() => onRemove(lineItem.localId)}
                    />
                  </div>
                </div>

                {isExpanded ? (
                  <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                    <div className="grid grid-cols-12 gap-3">
                      <DetailField
                        className="col-span-12 md:col-span-4"
                        label="Category"
                      >
                        <Input
                          size="sm"
                          wrapperClasses="mb-0"
                          value={lineItem.category}
                          onChange={(target) =>
                            onChange(lineItem.localId, {
                              category: target.value,
                            })
                          }
                        />
                      </DetailField>

                      <DetailField
                        className="col-span-6 md:col-span-3"
                        label="Service date"
                      >
                        <Input
                          size="sm"
                          wrapperClasses="mb-0"
                          type="date"
                          value={
                            lineItem.serviceDate
                              ? String(lineItem.serviceDate).slice(0, 10)
                              : ""
                          }
                          onChange={(target) =>
                            onChange(lineItem.localId, {
                              serviceDate: target.value || null,
                            })
                          }
                        />
                      </DetailField>

                      <DetailField
                        className="col-span-6 md:col-span-2"
                        label="Taxable"
                      >
                        <div className="flex h-full items-center rounded-lg border border-gray-200 bg-white px-3">
                          <Checkbox
                            id={`taxable-${lineItem.localId}`}
                            checked={Boolean(lineItem.taxable)}
                            onChange={(target) =>
                              onChange(lineItem.localId, {
                                taxable: target.checked,
                                taxRate: target.checked ? lineItem.taxRate : 0,
                                taxAmount: target.checked
                                  ? lineItem.taxAmount
                                  : 0,
                              })
                            }
                            label={lineItem.taxable ? "Tax applied" : "No tax"}
                          />
                        </div>
                      </DetailField>

                      <DetailField
                        className="col-span-6 md:col-span-2"
                        label="Tax rate %"
                      >
                        <Input
                          size="sm"
                          wrapperClasses="mb-0"
                          type="number"
                          value={formatTaxRatePercent(lineItem.taxRate)}
                          onChange={(target) =>
                            onChange(lineItem.localId, {
                              taxable: true,
                              taxRate: parseTaxRatePercent(target.value),
                            })
                          }
                        />
                      </DetailField>

                      <DetailField
                        className="col-span-6 md:col-span-2"
                        label="Tax amount"
                      >
                        <Input
                          size="sm"
                          wrapperClasses="mb-0"
                          type="number"
                          value={lineItem.taxAmount}
                          onChange={(target) =>
                            onChange(lineItem.localId, {
                              taxable: true,
                              taxAmount: toNumber(target.value),
                            })
                          }
                        />
                      </DetailField>

                      <DetailField
                        className="col-span-6 md:col-span-3"
                        label="Tax code"
                      >
                        <Input
                          size="sm"
                          wrapperClasses="mb-0"
                          value={lineItem.taxCode || ""}
                          placeholder="Optional tax code"
                          onChange={(target) =>
                            onChange(lineItem.localId, {
                              taxable: true,
                              taxCode: target.value,
                            })
                          }
                        />
                      </DetailField>
                    </div>

                    {isJobBacked ? (
                      <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                        This line is linked to a job. Adjust the billing fields
                        here without changing the underlying job record.
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      <Modal
        visible={jobPickerOpen}
        setVisible={setJobPickerOpen}
        title="Add jobs to invoice"
        size="lg"
        submitText={`Add ${selectedJobIds.length} job${
          selectedJobIds.length === 1 ? "" : "s"
        }`}
        cancelText="Cancel"
        confirm={confirmJobSelection}
        disabled={selectedJobIds.length === 0}
      >
        <div className="space-y-4">
          <Input
            label="Search jobs"
            placeholder="Search by job number or load number"
            value={jobSearch}
            clearable
            onChange={(target) =>
              setJobSearch(typeof target === "string" ? target : target.value)
            }
          />

          {availableJobs.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
              All available jobs are already attached to this invoice.
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
              No jobs match the current search.
            </div>
          ) : (
            <div className="max-h-[420px] space-y-3 overflow-y-auto pr-1">
              {filteredJobs.map((job) => {
                const checked = selectedJobIds.includes(String(job._id));

                return (
                  <div
                    key={job._id}
                    role="button"
                    tabIndex={0}
                    aria-pressed={checked}
                    onClick={() => toggleJobSelection(job._id, !checked)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleJobSelection(job._id, !checked);
                      }
                    }}
                    className={`cursor-pointer rounded-lg border px-4 py-3 outline-none transition ${
                      checked
                        ? "border-primary-300 bg-primary-50"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">
                          {getJobDisplayNumber(job)}
                        </p>
                        <p className="mt-1 text-sm text-gray-600">
                          {renderAmount(
                            job.totalCharges || job.calculatedTotalCharges || 0,
                          )}
                        </p>
                      </div>
                      <div onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          id={`job-select-${job._id}`}
                          checked={checked}
                          onChange={(target) =>
                            toggleJobSelection(job._id, target.checked)
                          }
                          label="Select"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default LineItemsEditor;
